import util from "../../styles/util.module.css";
import {
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Group,
  NumberInput,
  Radio,
  Select,
  Stepper,
  Table as MantineTable,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReturnsApi } from "../../apis/returns";
import { PageLayout } from "../../components/layout";
import { REQUEST_TYPE, SIMPLE_REASONS, RETURN_REASONS, getReasonLabel } from "../../utils/return-constants";
import { currencyFormat } from "../../utils";
import { useBreadcrumb } from "../../hooks/useBreadcrumb";
import { message } from "../../utils/Message";
import { handleError } from "../../utils/error-handler";
import classes from "./Create.module.css";

interface ReturnItem {
  orderItemId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  returnReason: string;
}

const EMPTY_ITEM: ReturnItem = {
  orderItemId: "",
  productName: "",
  quantity: 1,
  unitPrice: 0,
  returnReason: "",
};

const getReasonOptions = (requestType: string) => {
  const codes = requestType === "SIMPLE" ? SIMPLE_REASONS : RETURN_REASONS;
  return codes.map((code) => ({
    value: code,
    label: getReasonLabel(code),
  }));
};

type FormValues = {
  requestType: string;
  orderId: string;
  invoiceId: string;
  returnReason: string;
  reasonDetails: string;
  items: ReturnItem[];
  sellerBusinessId: string;
  evidenceUrls: string;
};

export const ReturnRequestCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("returns");
  const breadcrumbs = useBreadcrumb("returns", [
    { to: "/", key: "breadcrumb.home" },
    { to: "/refund-invoice", key: "breadcrumb.returns" },
    { key: "create.title" },
  ]);

  const [active, setActive] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    mode: "controlled",
    initialValues: {
      requestType: "SIMPLE",
      orderId: "",
      invoiceId: "",
      returnReason: "",
      reasonDetails: "",
      items: [{ ...EMPTY_ITEM }],
      sellerBusinessId: "",
      evidenceUrls: "",
    },
  });

  const parsedEvidenceUrls = form.values.evidenceUrls
    .split("\n")
    .map((u) => u.trim())
    .filter(Boolean);

  const reasonOptions = getReasonOptions(form.values.requestType);

  const handleRequestTypeChange = (value: string) => {
    form.setFieldValue("requestType", value);
    form.setFieldValue("returnReason", "");
    form.setFieldValue(
      "items",
      form.values.items.map((item) => ({
        ...item,
        returnReason: "",
      })),
    );
  };

  const handleAddItem = () => {
    form.setFieldValue("items", [...form.values.items, { ...EMPTY_ITEM }]);
  };

  const handleRemoveItem = (index: number) => {
    form.setFieldValue(
      "items",
      form.values.items.filter((_, i) => i !== index),
    );
  };

  const handleItemChange = (index: number, field: keyof ReturnItem, value: string | number) => {
    form.setFieldValue(
      "items",
      form.values.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const totalAmount = form.values.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);

  // Validation
  const canProceedToStep2 = !!form.values.requestType;

  const canProceedToStep3 =
    form.values.orderId.trim() !== "" &&
    form.values.returnReason !== "" &&
    (form.values.returnReason !== "OTHER" || form.values.reasonDetails.trim() !== "") &&
    form.values.items.length > 0 &&
    form.values.items.every(
      (item) =>
        item.orderItemId.trim() !== "" &&
        item.productName.trim() !== "" &&
        item.quantity >= 1 &&
        item.unitPrice >= 0 &&
        item.returnReason !== "",
    );

  const canSubmit =
    canProceedToStep3 &&
    form.values.sellerBusinessId.trim() !== "" &&
    (form.values.requestType !== "RETURN" || parsedEvidenceUrls.length >= 1);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const data = {
        requestType: form.values.requestType,
        orderId: form.values.orderId.trim(),
        invoiceId: form.values.invoiceId.trim() || null,
        returnType: form.values.items.length > 0 ? "PARTIAL" : "FULL",
        returnReason: form.values.returnReason,
        reasonDetails: form.values.reasonDetails.trim() || null,
        items: form.values.items.map((item) => ({
          orderItemId: item.orderItemId.trim(),
          quantity: item.quantity,
          returnReason: item.returnReason,
          unitPrice: item.unitPrice,
          productName: item.productName.trim(),
          productId: null,
          variantName: null,
          sku: null,
        })),
        evidencePhotos: parsedEvidenceUrls,
        sellerBusinessId: form.values.sellerBusinessId.trim(),
        sellerBusinessName: null,
        buyerBusinessName: null,
        orderNumber: null,
        invoiceNumber: null,
      };

      const res = await ReturnsApi.create(data);

      if (form.values.requestType === "SIMPLE") {
        message.success(
          `${t("create.successSimple")} ${res?.refundAmount ? `${t("create.refundLabel")} ${currencyFormat(res.refundAmount)}` : ""}${res?.creditNoteNumber ? ` (${res.creditNoteNumber})` : ""}`,
        );
      } else {
        message.success(t("create.successReturn"));
      }

      navigate(`/refund-invoice/${res?.returnRequestId || ""}`);
    } catch (error) {
      handleError(error, t("create.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout title={t("create.title")} subTitle={t("create.subtitle")} breadcrumb={breadcrumbs}>
      <Box className={classes.container}>
        <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
          {/* Step 1: Request type selection */}
          <Stepper.Step label={t("create.step1Label")} description={t("create.step1Desc")}>
            <Box className={classes.stepContent}>
              <Title order={4} mb="md">
                {t("create.step1Title")}
              </Title>

              <Radio.Group {...form.getInputProps("requestType")} onChange={handleRequestTypeChange}>
                <Flex direction="column" gap="md" mt="xs">
                  <Radio
                    value="SIMPLE"
                    label={`${REQUEST_TYPE.SIMPLE.label} - ${t("create.simpleAutoLabel")}`}
                  />
                  <Radio
                    value="RETURN"
                    label={`${REQUEST_TYPE.RETURN.label} - ${t("create.returnManualLabel")}`}
                  />
                </Flex>
              </Radio.Group>

              <Alert color={form.values.requestType === "SIMPLE" ? "gray" : "gray"} mt="lg" variant="light">
                {form.values.requestType === "SIMPLE" ? (
                  <Text size="sm">
                    {t("create.step1SimpleDesc")}
                  </Text>
                ) : (
                  <Text size="sm">
                    {t("create.step1ReturnDesc")}
                  </Text>
                )}
              </Alert>

              <Group mt="lg" justify="flex-end">
                <Button rightSection={<ArrowRightIcon size={16} />} onClick={() => setActive(1)} disabled={!canProceedToStep2}>
                  {t("action.next")}
                </Button>
              </Group>
            </Box>
          </Stepper.Step>

          {/* Step 2: Order + Items */}
          <Stepper.Step label={t("create.step2Label")} description={t("create.step2Desc")}>
            <Box className={classes.stepContent}>
              <Title order={4} mb="md">
                {t("create.step2Title")}
              </Title>

              <Grid>
                <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
                  <TextInput
                    label={t("create.orderId")}
                    placeholder={t("create.orderId")}
                    {...form.getInputProps("orderId")}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
                  <TextInput
                    label={t("create.invoiceId")}
                    placeholder={t("create.optionalPlaceholder")}
                    {...form.getInputProps("invoiceId")}
                  />
                </Grid.Col>
              </Grid>

              <Grid mt="sm">
                <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
                  <Select
                    label={t("create.returnReason")}
                    placeholder={t("create.reasonPlaceholder")}
                    data={reasonOptions}
                    {...form.getInputProps("returnReason")}
                    required
                  />
                </Grid.Col>
                {form.values.returnReason === "OTHER" && (
                  <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
                    <Textarea
                      label={t("create.reasonDetails")}
                      placeholder={t("create.reasonDetailsPlaceholder")}
                      {...form.getInputProps("reasonDetails")}
                      minRows={2}
                      required
                    />
                  </Grid.Col>
                )}
              </Grid>

              <Divider my="md" />

              <Flex justify="space-between" align="center" mb="sm">
                <Title order={5}>{t("create.productName")} ({form.values.items.length})</Title>
                <Button size="xs" variant="light" leftSection={<PlusIcon size={14} />} onClick={handleAddItem}>
                  {t("create.addItem")}
                </Button>
              </Flex>

              <MantineTable horizontalSpacing="md" verticalSpacing="sm" striped="even" highlightOnHover>
                <MantineTable.Thead>
                  <MantineTable.Tr>
                    <MantineTable.Th>#</MantineTable.Th>
                    <MantineTable.Th>Order Item ID</MantineTable.Th>
                    <MantineTable.Th>{t("create.productName")}</MantineTable.Th>
                    <MantineTable.Th ta="center">{t("create.qty")}</MantineTable.Th>
                    <MantineTable.Th>{t("create.unitPrice")}</MantineTable.Th>
                    <MantineTable.Th>{t("item.reason")}</MantineTable.Th>
                    <MantineTable.Th w={50}></MantineTable.Th>
                  </MantineTable.Tr>
                </MantineTable.Thead>
                <MantineTable.Tbody>
                  {form.values.items.map((item, index) => (
                    <MantineTable.Tr key={index}>
                      <MantineTable.Td>
                        <Text size="sm">{index + 1}</Text>
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <TextInput
                          size="xs"
                          w={140}
                          placeholder="Item ID"
                          value={item.orderItemId}
                          onChange={(e) => handleItemChange(index, "orderItemId", e.currentTarget.value)}
                        />
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <TextInput
                          size="xs"
                          w={180}
                          placeholder={t("create.productName")}
                          value={item.productName}
                          onChange={(e) => handleItemChange(index, "productName", e.currentTarget.value)}
                        />
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <NumberInput
                          size="xs"
                          w={80}
                          min={1}
                          value={item.quantity}
                          onChange={(value) => handleItemChange(index, "quantity", value || 1)}
                        />
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <NumberInput
                          size="xs"
                          w={120}
                          min={0}
                          decimalScale={2}
                          value={item.unitPrice}
                          onChange={(value) => handleItemChange(index, "unitPrice", value || 0)}
                        />
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <Select
                          size="xs"
                          w={180}
                          placeholder={t("create.selectPlaceholder")}
                          data={reasonOptions}
                          value={item.returnReason}
                          onChange={(value) => handleItemChange(index, "returnReason", value || "")}
                        />
                      </MantineTable.Td>
                      <MantineTable.Td>
                        {form.values.items.length > 1 && (
                          <Button size="xs" variant="subtle" color="red" p={4} onClick={() => handleRemoveItem(index)}>
                            <XIcon size={14} />
                          </Button>
                        )}
                      </MantineTable.Td>
                    </MantineTable.Tr>
                  ))}
                </MantineTable.Tbody>
              </MantineTable>

              {form.values.items.length > 0 && (
                <Alert color="gray" mt="md" variant="light">
                  <Flex justify="space-between" align="center">
                    <Text size="sm">
                      {t("create.totalItems")}: <Text span fw={600}>{form.values.items.length}</Text>
                    </Text>
                    <Text size="sm">
                      {t("create.totalAmount")}: <Text span fw={600}>{currencyFormat(totalAmount)}</Text>
                    </Text>
                  </Flex>
                </Alert>
              )}

              <Group mt="md" justify="space-between">
                <Button variant="default" leftSection={<ArrowLeftIcon size={16} />} onClick={() => setActive(0)}>
                  {t("action.back")}
                </Button>
                <Button rightSection={<ArrowRightIcon size={16} />} onClick={() => setActive(2)} disabled={!canProceedToStep3}>
                  {t("action.next")}
                </Button>
              </Group>
            </Box>
          </Stepper.Step>

          {/* Step 3: Additional info + Summary */}
          <Stepper.Step label={t("create.step3Label")} description={t("create.step3Desc")}>
            <Box className={classes.stepContent}>
              <Title order={4} mb="md">
                {t("create.step3Title")}
              </Title>

              <Grid>
                <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
                  <TextInput
                    label={t("create.sellerBusinessId")}
                    placeholder="Seller Business ID"
                    {...form.getInputProps("sellerBusinessId")}
                    required
                  />
                </Grid.Col>
              </Grid>

              <Alert color="yellow" mt="md" variant="light" title={t("create.evidenceTitle")}>
                <Text size="sm">{t("create.evidenceDesc")}</Text>
              </Alert>

              <Textarea
                mt="sm"
                label={t("create.evidenceUrls")}
                placeholder={t("create.evidenceUrlsPlaceholder")}
                {...form.getInputProps("evidenceUrls")}
                minRows={2}
                required={form.values.requestType === "RETURN"}
                error={
                  form.values.requestType === "RETURN" && form.values.evidenceUrls.trim() !== "" && parsedEvidenceUrls.length === 0
                    ? t("create.errEvidence")
                    : undefined
                }
                description={form.values.requestType === "RETURN" ? t("create.errEvidence") : undefined}
              />

              <Divider my="md" />

              <Title order={4} mb="md">
                {t("create.summaryTitle")}
              </Title>

              <Grid>
                <Grid.Col span={{ xs: 12, md: 6 }}>
                  <Box className={classes.summarySection}>
                    <Text size="sm" c="dimmed">{t("create.returnType")}</Text>
                    <Badge color={form.values.requestType === "SIMPLE" ? "gray" : "gray"} variant="light">
                      {form.values.requestType === "SIMPLE" ? REQUEST_TYPE.SIMPLE.label : REQUEST_TYPE.RETURN.label}
                    </Badge>
                  </Box>
                  <Box className={classes.summarySection}>
                    <Text size="sm" c="dimmed">{t("create.orderId")}</Text>
                    <Text size="sm" fw={600}>{form.values.orderId}</Text>
                  </Box>
                  {form.values.invoiceId && (
                    <Box className={classes.summarySection}>
                      <Text size="sm" c="dimmed">{t("create.invoiceId")}</Text>
                      <Text size="sm" fw={600}>{form.values.invoiceId}</Text>
                    </Box>
                  )}
                  <Box className={classes.summarySection}>
                    <Text size="sm" c="dimmed">{t("detail.reason")}</Text>
                    <Text size="sm" fw={600}>{getReasonLabel(form.values.returnReason)}</Text>
                  </Box>
                  {form.values.reasonDetails && (
                    <Box className={classes.summarySection}>
                      <Text size="sm" c="dimmed">{t("create.reasonDetails")}</Text>
                      <Text size="sm">{form.values.reasonDetails}</Text>
                    </Box>
                  )}
                  <Box className={classes.summarySection}>
                    <Text size="sm" c="dimmed">{t("create.sellerBusinessId")}</Text>
                    <Text size="sm" fw={600}>{form.values.sellerBusinessId}</Text>
                  </Box>
                </Grid.Col>
              </Grid>

              <Divider my="md" />

              <Title order={5} mb="sm">
                {t("create.summaryItems")} ({form.values.items.length})
              </Title>

              <MantineTable horizontalSpacing="md" verticalSpacing="sm" striped="even">
                <MantineTable.Thead>
                  <MantineTable.Tr>
                    <MantineTable.Th>#</MantineTable.Th>
                    <MantineTable.Th>Order Item ID</MantineTable.Th>
                    <MantineTable.Th>{t("item.name")}</MantineTable.Th>
                    <MantineTable.Th ta="center">{t("create.qty")}</MantineTable.Th>
                    <MantineTable.Th>{t("create.unitPrice")}</MantineTable.Th>
                    <MantineTable.Th>{t("item.total")}</MantineTable.Th>
                    <MantineTable.Th>{t("item.reason")}</MantineTable.Th>
                  </MantineTable.Tr>
                </MantineTable.Thead>
                <MantineTable.Tbody>
                  {form.values.items.map((item, index) => (
                    <MantineTable.Tr key={index}>
                      <MantineTable.Td>
                        <Text size="sm">{index + 1}</Text>
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <Text size="sm" c="dimmed">{item.orderItemId}</Text>
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <Text size="sm">{item.productName}</Text>
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <Text size="sm" ta="center">{item.quantity}</Text>
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <Text size="sm" className={util.nowrap}>{currencyFormat(item.unitPrice)}</Text>
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <Text size="sm" fw={600} className={util.nowrap}>{currencyFormat(item.quantity * item.unitPrice)}</Text>
                      </MantineTable.Td>
                      <MantineTable.Td>
                        <Text size="sm">{getReasonLabel(item.returnReason)}</Text>
                      </MantineTable.Td>
                    </MantineTable.Tr>
                  ))}
                </MantineTable.Tbody>
              </MantineTable>

              <Alert color="gray" mt="md" variant="light">
                <Flex justify="flex-end">
                  <Text size="md" fw={700}>
                    {t("create.totalRequestAmount")}: {currencyFormat(totalAmount)}
                  </Text>
                </Flex>
              </Alert>

              <Group mt="lg" justify="space-between">
                <Button variant="default" leftSection={<ArrowLeftIcon size={16} />} onClick={() => setActive(1)}>
                  {t("action.back")}
                </Button>
                <Button leftSection={<CheckIcon size={16} />} color="brand" onClick={handleSubmit} loading={submitting} disabled={!canSubmit}>
                  {t("action.submit")}
                </Button>
              </Group>
            </Box>
          </Stepper.Step>
        </Stepper>
      </Box>
    </PageLayout>
  );
};
